import React from 'react';
import './Hero.css';
import { Star, MapPin, Clock, Phone } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

import optimized1 from '../images/optimized/1.jpg';

const Hero = () => {
  const { language, t } = useLanguage();

  const scrollToMenu = (e) => {
    e.preventDefault();
    const element = document.querySelector('#menu');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        backgroundImage: `url(${optimized1})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Overlay */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, rgba(26, 26, 26, 0.7), rgba(74, 55, 40, 0.85))'
        }}
      />

      {/* Content */}
      <div className="container relative z-10 py-32">
        <div className="max-w-4xl mx-auto text-center">
          {/* Title */}
          <h1 className="hero-title mb-4 text-4xl md:text-6xl animate-in fade-in slide-in-from-bottom-4 duration-700">
            {t.hero.title}
          </h1>
          <p
            className="text-2xl md:text-3xl mb-3 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100"
            style={{ color: 'var(--amber-light)' }}
          >
            {t.hero.subtitle}
          </p>
          <p className="text-lg md:text-xl text-white/90 mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
            {t.hero.description}
          </p>

          {/* Badges */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
            <Badge className="bg-white/95 text-base py-2 px-4" style={{ color: 'var(--brown-dark)' }}>
              <Star size={18} fill="var(--amber)" stroke="var(--amber)" />
              <span className="font-semibold">{t.hero.rating}</span>
              <span>({t.hero.reviews})</span>
            </Badge>
            <Badge className="bg-white/95 text-base py-2 px-4" style={{ color: 'var(--brown-dark)' }}>
              {t.hero.type}
            </Badge>
          </div>

          {/* Info */}
          <div className="flex flex-col gap-3 mb-10 text-white/90 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-400">
            <div className="flex items-center justify-center gap-2">
              <MapPin size={20} style={{ color: 'var(--amber-light)' }} />
              <span>{t.hero.address}</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Clock size={20} style={{ color: 'var(--amber-light)' }} />
              <span>{t.hero.hours}</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-4 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-500">
            <Button
              onClick={scrollToMenu}
              size="lg"
              className="btn-primary text-lg px-8 py-6"
            >
              {t.hero.viewMenu}
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6 bg-white/95 border-white hover:bg-white"
            >
              <a href="tel:+37378015880" className="flex items-center gap-2">
                <Phone size={20} />
                {t.hero.callNow}
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-white/70 rounded-full" />
        </div>
      </div>
    </section>
  );
};

export default Hero;